import { useMemo } from "react";
import { MutateOptions, useMutation, UseMutationOptions } from "@tanstack/react-query";
import { UpdateCardQuery } from "../../../api/query.types";
import { SavedShipmentResponseDataDTO } from "../../../api/response.types";

import { savedShipmentService } from "../../../api/api";
import { SeaSavedCardModel } from "../../../model/model";

type UpdateCardResponse = SavedShipmentResponseDataDTO;
type UpdateCardError = Error;
export type UpdateCardFunc = (
  data: UpdateCardQuery,
  options?: Omit<MutateOptions<UpdateCardResponse, UpdateCardError, UpdateCardQuery>, "onSuccess" | "onSettled"> & {
    onSuccess?: (data: SeaSavedCardModel) => void;
    onSettled?: (data: SeaSavedCardModel | undefined, error: Error | null, variables: UpdateCardQuery) => void;
  }
) => void;

type UseUpdateSingleCardMutationReturnType = {
  updateCard: UpdateCardFunc;
  updatedCard: SeaSavedCardModel | undefined;
  isCardUpdateSuccess: boolean;
  isCardUpdateError: boolean;
  isCardUpdateLoading: boolean;
};

export const useUpdateSingleCardMutation = (
  mutationOptions?: UseMutationOptions<SavedShipmentResponseDataDTO, Error, UpdateCardQuery>
): UseUpdateSingleCardMutationReturnType => {
  const { data, mutate, isError, isPending, isSuccess } = useMutation<
    UpdateCardResponse,
    UpdateCardError,
    UpdateCardQuery
  >({
    mutationFn: savedShipmentService.updateCard,
    ...mutationOptions,
  });

  const updateCard: UpdateCardFunc = (variables, { onSuccess, onSettled, ...rest } = {}) => {
    return mutate(variables, {
      ...rest,
      onSuccess: (data) => {
        onSuccess?.(new SeaSavedCardModel(data));
      },
      onSettled: (data, error, variables) => {
        onSettled?.(data ? new SeaSavedCardModel(data) : undefined, error, variables);
      },
    });
  };

  const updatedCard = useMemo<SeaSavedCardModel | undefined>(() => {
    if (!data) return;
    return new SeaSavedCardModel(data);
  }, [data]);

  return {
    updateCard,
    updatedCard,
    isCardUpdateSuccess: isSuccess,
    isCardUpdateError: isError,
    isCardUpdateLoading: isPending,
  };
};
